import React, { useState } from 'react';
import { signIn, confirmSignIn, resetPassword, confirmResetPassword, resendSignUpCode, confirmSignUp } from 'aws-amplify/auth';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Box,
  Typography,
  Link,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  VpnKey,
} from '@mui/icons-material';

// Props: open, onClose, onSuccess, onSwitchToSignUp
const LoginForm = ({ 
  open, 
  onClose, 
  onSuccess, 
  onSwitchToSignUp 
}) => {
  const { refreshUser } = useAuth();
  const [step, setStep] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const getUsername = () => email.trim().toLowerCase();

  const formatAuthError = (err) => {
    const errorMap = {
      NotAuthorizedException: 'Incorrect email or password. Please try again or reset your password.',
      UserNotFoundException: 'No account found with this email address. Please check the email or create a new account.',
      UserNotConfirmedException: 'Your email is not verified. Click "Send Verification Code" below to verify your account.',
      PasswordResetRequiredException: 'A password reset is required for this account. Please use "Forgot Password".',
      TooManyRequestsException: 'Too many attempts. Please wait a few minutes and try again.',
      CodeMismatchException: 'Invalid verification code. Please try again.',
      ExpiredCodeException: 'Verification code has expired. Please request a new code.',
      LimitExceededException: 'Too many attempts. Please wait before trying again.',
    };

    return errorMap[err?.name] || err?.message || 'Authentication failed. Please try again.';
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(prev => !prev);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const username = getUsername();
      if (!username || !password) {
        setError('Please enter your email address and password.');
        return;
      }

      console.log('🔐 Starting MammoTrials sign in...');
      const signInResult = await signIn({ username, password });
      console.log('✅ Sign in successful:', signInResult);

      if (!signInResult.isSignedIn) {
        const signInStep = signInResult.nextStep?.signInStep;
        
        if (signInStep === 'CONFIRM_SIGN_UP') {
          setError('Your email is not verified. Click "Send Verification Code" below to verify your account.');
          setStep('resend-verification');
          return;
        }
        
        if (signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED' || signInStep === 'FORCE_CHANGE_PASSWORD') {
          setError('You must set a new password. Please enter a new password below.');
          setStep('force-change-password');
          return;
        }

        setError('Additional sign-in verification is required. Please complete verification and try again.');
        console.log('🔍 Unhandled sign-in step:', signInStep, signInResult.nextStep);
        return;
      }
      
      // Wait for token storage and refresh user context
      console.log('🔄 Refreshing user context...');
      await new Promise(resolve => setTimeout(resolve, 500));
      await refreshUser();
      
      onSuccess();
      handleClose();
      
    } catch (err) {
      if (err?.name === 'UserNotFoundException') {
        console.info('ℹ️ Sign in failed: user does not exist for this user pool.');
      } else {
        console.error('❌ Sign in error:', err);
      }

      const formattedError = formatAuthError(err);
      setError(formattedError);

      // Specific handling for unverified users
      if (err.name === 'UserNotConfirmedException') {
        setStep('resend-verification');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForcePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (!newPassword) {
        setError('Please enter a new password.');
        return;
      }

      if (newPassword.length < 8) {
        setError('Password must be at least 8 characters long.');
        return;
      }

      console.log('🔒 Confirming new password...');
      const confirmResult = await confirmSignIn({ challengeResponse: newPassword });
      
      if (confirmResult.isSignedIn) {
        console.log('✅ Password changed and signed in successfully');
        
        // Wait for token storage and refresh user context
        await new Promise(resolve => setTimeout(resolve, 500));
        await refreshUser();
        
        onSuccess();
        handleClose();
      } else {
        setError('Failed to complete password change. Please try again.');
      }
      
    } catch (err) {
      console.error('❌ Password change error:', err);
      const formattedError = formatAuthError(err);
      setError(formattedError);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const username = getUsername();
      if (!username) {
        setError('Please enter your email address.');
        return;
      }

      console.log('📧 Initiating password reset...');
      await resetPassword({ username });
      setSuccess(`Password reset code sent to ${email}. Please check your email.`);
      setStep('reset-code');
      
    } catch (err) {
      console.error('❌ Password reset error:', err);
      setError(formatAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const username = getUsername();
      if (!username || !resetCode || !newPassword) {
        setError('Please fill in all fields.');
        return;
      }

      console.log('🔑 Confirming password reset...');
      await confirmResetPassword({
        username,
        confirmationCode: resetCode,
        newPassword,
      });

      setSuccess('Password updated successfully! You can now sign in with your new password.');
      setStep('login');
      setPassword(newPassword); // Pre-fill the new password
      setResetCode('');
      setNewPassword('');
      
    } catch (err) {
      console.error('❌ Password reset confirmation error:', err);
      setError(formatAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const username = getUsername();
      if (!username) {
        setError('Please enter your email address.');
        return;
      }

      console.log('📨 Resending verification code...');
      await resendSignUpCode({ username });
      setSuccess(`Verification code sent to ${email}. Please check your email.`);
      
    } catch (err) {
      console.error('❌ Resend verification error:', err);
      setError(formatAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const username = getUsername();
      if (!username || !verificationCode) {
        setError('Please enter your email address and verification code.');
        return;
      }

      console.log('✅ Confirming sign up...');
      await confirmSignUp({
        username,
        confirmationCode: verificationCode,
      });

      setSuccess('Email verified successfully! You can now sign in.');
      setStep('login');
      setVerificationCode('');
      
    } catch (err) {
      console.error('❌ Sign up confirmation error:', err);
      setError(formatAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form state
    setStep('login');
    setEmail('');
    setPassword('');
    setNewPassword('');
    setResetCode('');
    setVerificationCode('');
    setError('');
    setSuccess('');
    setLoading(false);
    setShowPassword(false);
    setShowNewPassword(false);
    onClose();
  };

  const getTitle = () => {
    switch (step) {
      case 'forgot-password': return 'Reset Password';
      case 'reset-code': return 'Enter Reset Code';
      case 'resend-verification': return 'Verify Email';
      case 'force-change-password': return 'Set New Password';
      default: return 'Sign In to MammoTrials';
    }
  };

  const renderLoginStep = () => (
    <Box component="form" onSubmit={handleLogin} sx={{ mt: 2 }}>
      <TextField
        fullWidth
        label="Email Address"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        margin="normal"
        required
        disabled={loading}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Email color="primary" />
            </InputAdornment>
          ),
        }}
      />
      
      <TextField
        fullWidth
        label="Password"
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        margin="normal"
        required
        disabled={loading}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock color="primary" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => setStep('forgot-password')}
          disabled={loading}
          sx={{ textDecoration: 'none' }}
        >
          Forgot Password?
        </Link>
        
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => setStep('resend-verification')}
          disabled={loading}
          sx={{ textDecoration: 'none' }}
        >
          Resend Verification?
        </Link>
      </Box>

      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={loading || !email || !password}
        sx={{ mt: 3, mb: 2, height: 48 }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
      </Button>

      <Typography variant="body2" align="center" sx={{ mt: 2 }}>
        Don't have an account?{' '}
        <Link
          component="button"
          type="button"
          onClick={onSwitchToSignUp}
          disabled={loading}
          sx={{ textDecoration: 'none', fontWeight: 'bold' }}
        >
          Create Account
        </Link>
      </Typography>
    </Box>
  );

  const renderForgotPasswordStep = () => (
    <Box component="form" onSubmit={handleForgotPassword} sx={{ mt: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter your email address and we'll send you a code to reset your password.
      </Typography>
      
      <TextField
        fullWidth
        label="Email Address"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        margin="normal"
        required
        disabled={loading}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Email color="primary" />
            </InputAdornment>
          ),
        }}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={loading || !email}
        sx={{ mt: 3, mb: 2, height: 48 }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Code'}
      </Button>

      <Button
        fullWidth
        variant="text"
        onClick={() => setStep('login')}
        disabled={loading}
      >
        Back to Sign In
      </Button>
    </Box>
  );

  const renderResetCodeStep = () => (
    <Box component="form" onSubmit={handleResetPassword} sx={{ mt: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter the reset code from your email and your new password.
      </Typography>
      
      <TextField
        fullWidth
        label="Reset Code"
        value={resetCode}
        onChange={(e) => setResetCode(e.target.value)}
        margin="normal"
        required
        disabled={loading}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <VpnKey color="primary" />
            </InputAdornment>
          ),
        }}
      />
      
      <TextField
        fullWidth
        label="New Password"
        type={showNewPassword ? 'text' : 'password'}
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        margin="normal"
        required
        disabled={loading}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock color="primary" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowNewPassword(!showNewPassword)}
                disabled={loading}
                edge="end"
              >
                {showNewPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={loading || !resetCode || !newPassword}
        sx={{ mt: 3, mb: 2, height: 48 }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
      </Button>

      <Button
        fullWidth
        variant="text"
        onClick={() => setStep('forgot-password')}
        disabled={loading}
      >
        Resend Reset Code
      </Button>
    </Box>
  );

  const renderVerificationStep = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Please verify your email address to complete registration.
      </Typography>
      
      <Box component="form" onSubmit={handleResendVerification} sx={{ mb: 2 }}>
        <TextField
          fullWidth
          label="Email Address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          margin="normal"
          required
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email color="primary" />
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="outlined"
          disabled={loading || !email}
          sx={{ mt: 2, mb: 2, height: 48 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Verification Code'}
        </Button>
      </Box>

      <Box component="form" onSubmit={handleConfirmSignUp}>
        <TextField
          fullWidth
          label="Verification Code"
          value={verificationCode}
          onChange={(e) => setVerificationCode(e.target.value)}
          margin="normal"
          required
          disabled={loading}
          placeholder="Enter 6-digit code from email"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <VpnKey color="primary" />
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading || !email || !verificationCode}
          sx={{ mt: 3, mb: 2, height: 48 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify Email'}
        </Button>
      </Box>

      <Button
        fullWidth
        variant="text"
        onClick={() => setStep('login')}
        disabled={loading}
      >
        Back to Sign In
      </Button>
    </Box>
  );

  const renderForcePasswordChangeStep = () => (
    <Box component="form" onSubmit={handleForcePasswordChange} sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        For security, you must set a new password before continuing.
      </Typography>
      
      <TextField
        fullWidth
        label="New Password"
        type={showNewPassword ? 'text' : 'password'}
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        required
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={toggleNewPasswordVisibility} edge="end">
                {showNewPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        helperText="Password must be at least 8 characters"
      />
      
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={loading || !newPassword}
        sx={{ mt: 2 }}
      >
        {loading ? 'Setting Password...' : 'Set New Password'}
      </Button>
      
      <Button
        fullWidth
        variant="text"
        onClick={() => setStep('login')}
        sx={{ mt: 1 }}
      >
        Back to Sign In
      </Button>
    </Box>
  );

  const renderCurrentStep = () => {
    switch (step) {
      case 'forgot-password': return renderForgotPasswordStep();
      case 'reset-code': return renderResetCodeStep();
      case 'resend-verification': return renderVerificationStep();
      case 'force-change-password': return renderForcePasswordChangeStep();
      default: return renderLoginStep();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 2,
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Typography variant="h5" component="span" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          {getTitle()}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {renderCurrentStep()}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', pt: 0 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          color="inherit"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoginForm;